import { useEffect, useState } from "react";
import { Images, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";

const Stat = ({ label, value, tone = "text-navy", testId }) => (
  <div className="rounded-xl bg-canvas p-3"><div className="text-xs text-slate-500">{label}</div><div className={`mt-1 text-sm font-semibold ${tone}`} data-testid={testId}>{value ?? "—"}</div></div>
);

export const PhotoSourceCard = ({ settings, sources, run, onChanged }) => {
  const { t, lang } = useI18n();
  const [source, setSource] = useState(settings.source);
  const [busy, setBusy] = useState(false);
  const running = !!run?.running;
  useEffect(() => { if (!running) return; const id = setInterval(onChanged, 3000); return () => clearInterval(id); }, [running, onChanged]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/catalogue-sync/photo-settings", { source, eshop_url_template: "" });
      toast.success(t("photos.saved"));
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalogue-sync/photos/run");
      toast[data.started ? "success" : "info"](data.started ? t("photos.started") : t("photos.running"));
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm" data-testid="photo-source-card">
      <div>
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("photos.title")}</h2>
        <p className="text-xs text-slate-500">{t("photos.subtitle")}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
        {sources.map((s) => (
          <button key={s} type="button" role="radio" aria-checked={source === s} onClick={() => setSource(s)} data-testid={`photo-source-${s}`}
            className={`min-h-11 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${source === s ? "border-brand bg-brand/10 text-navy" : "border-slate-200 text-slate-600 hover:border-navy/30"}`}>
            <div className="font-semibold">{t(`photos.source.${s}`)}</div>
            <div className="text-xs text-slate-500">{t(`photos.hint.${s}`)}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={busy || source === settings.source} data-testid="photo-settings-save" className="h-11 rounded-full bg-navy px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
          {t("photos.save")}
        </button>
        <button type="button" onClick={syncNow} disabled={busy || running || settings.source === "placeholder"} data-testid="photo-sync-now" className="flex h-11 items-center gap-2 rounded-full border border-navy/15 px-5 text-sm font-semibold text-navy transition-colors hover:bg-canvas disabled:opacity-50">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />} {running ? t("photos.running") : t("photos.syncNow")}
        </button>
        {run?.finished_at && !running && <span className="text-xs text-slate-500" data-testid="photo-run-finished">{t("photos.lastRun")}: {new Date(run.finished_at).toLocaleString(lang === "en" ? "en-GB" : "el-GR")} · {run.status}</span>}
        {running && run?.total != null && <span className="text-xs text-amber-700" data-testid="photo-run-progress">{run.done} / {run.total}</span>}
      </div>
      {run && (run.matched != null || run.processed != null) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="photo-run-stats">
          <Stat label={t("photos.matched")} value={run.matched != null ? `${run.matched} / ${run.eshop_products}` : null} tone="text-emerald-700" testId="photo-stat-matched" />
          <Stat label={t("photos.notOnEshop")} value={run.not_on_eshop} tone="text-slate-600" testId="photo-stat-not-on-eshop" />
          <Stat label={t("photos.processed")} value={run.processed} testId="photo-stat-processed" />
          <Stat label={t("photos.unchanged")} value={run.unchanged} testId="photo-stat-unchanged" />
          <Stat label={t("photos.missing")} value={run.missing} tone="text-amber-700" testId="photo-stat-missing" />
          <Stat label={t("photos.failed")} value={run.failed} tone={run.failed ? "text-rose-700" : "text-navy"} testId="photo-stat-failed" />
        </div>
      )}
      {run?.error_sample?.length > 0 && <pre className="max-h-32 overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100" data-testid="photo-run-errors">{run.error_sample.join("\n")}</pre>}
    </section>
  );
};
